import { z } from 'zod';

import type { TemplateName } from '../core/assetType.js';

import { TEMPLATE_NAMES } from '../core/assetType.js';

/**
 * Shared schemas for the learning-asset envelope. All four templates
 * (story / lesson / gameshow / dil) are wrapped in the same envelope; the
 * envelope's discriminator is `fields.templateName`, and the inner data always
 * hangs off `content[0].learningAssetContentItems[0].storyData` — yes, even
 * for lessons / gameshows / dil. Only the inner SHAPE varies per template;
 * the inner KEY is uniformly `storyData`, matching the "everything is
 * story_asset" backend convention.
 *
 * Constants enforced at schema level:
 *   - fields.coinValue.coins must be 5
 *   - schemaVersion must be 1
 *   - All `type` fields (content[].type, learningAssetContentItems[].type,
 *     fields.type) are forced to the literal string "story_asset" — yes, even
 *     for lessons / gameshows / dil. This is a backend convention.
 */

export const STORY_ASSET = 'story_asset' as const;

/** The inner data key inside learningAssetContentItems[0]. Always "storyData". */
export const INNER_DATA_KEY = 'storyData' as const;
export type InnerDataKey = typeof INNER_DATA_KEY;

export const RIASEC_CODES = ['R', 'I', 'A', 'S', 'E', 'C'] as const;

export const CoinValue = z.looseObject({ coins: z.literal(5) });

/**
 * Cover image reference. New hand-authored assets use a plain URL string (or
 * null). Assets pulled from the CMS carry the stored file-reference OBJECT
 * (`{ path, disk, originalName, ... }`, with an `id` only on some exports). Both
 * are valid; accept either so DB-sourced envelopes parse and their node-level
 * rules can run.
 */
export const CoverImageObject = z.looseObject({
  id: z.string().min(1).optional(),
  path: z.string().min(1),
});
export const CoverImage = z.union([z.string(), z.null(), CoverImageObject]);

/** ProseMirror-like doc; we only care that `type === "doc"` and `content` is an array. */
export const StructuredLearningOutcomeDescription = z.looseObject({
  type: z.literal('doc'),
  content: z.array(z.unknown()),
});

export const StructuredLearningOutcome = z.looseObject({
  title: z.string().min(1),
  description: StructuredLearningOutcomeDescription,
});

export const EnvelopeMeta = z.looseObject({
  title: z.string().min(1),
  description: z.string().min(1),
});

/** A job referenced by the indexing block: stable CMS id + display title. */
export const IndexingJob = z.looseObject({
  id: z.string().min(1),
  title: z.string().min(1),
});

/**
 * Search/indexing metadata — the CANONICAL home for the asset's taxonomy. The
 * career cluster and RIASEC codes live here and ONLY here; flat
 * `fields.careerCluster` / `fields.riasecCodes` mirrors are forbidden by the
 * envelopeFlatTaxonomy rule. `skillsList` is the canonical list of skill slugs
 * (a top-level `skills` array is likewise forbidden, by envelopeSkills), and
 * `jobsList` is the roles this asset covers (nullable until populated).
 * `riasecSkills` is exactly two RIASEC codes. Membership of
 * careerCluster/skillsList against the registries and the non-empty
 * requirement on skillsList are enforced by the envelopeIndexing rule.
 */
export const Indexing = z.looseObject({
  careerCluster: z.string().min(1),
  jobsList: z.array(IndexingJob).nullable(),
  riasecSkills: z.array(z.enum(RIASEC_CODES)).length(2),
  skillsList: z.array(z.string().min(1)),
});

export const EnvelopeFields = z.looseObject({
  coinValue: CoinValue,
  coverImage: CoverImage,
  duration: z.number().positive(),
  internalName: z.string().min(1),
  name: z.string().min(1),
  slug: z.string().min(1),
  // Required-but-nullable, the same shape the read contract gives it: the
  // mapper emits `subtitle` on every asset, so a document that omits it is not
  // one the API could ever have served. Most assets have none — say so with an
  // explicit null rather than by leaving the key out.
  subtitle: z.string().nullable(),
  type: z.literal(STORY_ASSET),
  templateName: z.enum(TEMPLATE_NAMES),
  order: z.number().nullable(),
  publishedAt: z.union([z.string(), z.null()]),
  // The career cluster and RIASEC codes live solely in `indexing` (see above);
  // flat `careerCluster` / `riasecCodes` mirrors are forbidden by the
  // envelopeFlatTaxonomy rule. Indexing metadata + consideration-area slugs are
  // optional at the schema level so existing assets still parse (and other rules
  // still run); presence and registry membership are enforced by dedicated
  // envelope rules.
  indexing: Indexing.optional(),
  considerationAreaSlugs: z.array(z.string().min(1)).nullable().optional(),
  meta: EnvelopeMeta,
  structuredLearningOutcome: StructuredLearningOutcome,
});

/**
 * Builds an envelope schema for a given inner schema. The inner key is always
 * `storyData` regardless of `fields.templateName`; only the SHAPE differs per
 * template.
 *
 * When `templateName` is given, `fields.templateName` is pinned to that
 * literal — so validating a document against the wrong template's schema
 * (an explicit dispatch override that contradicts the document) fails loudly
 * instead of passing the shared envelope checks.
 *
 * The validator expects:
 *   envelope.content[0].learningAssetContentItems[0].storyData -> inner
 */
export const makeEnvelopeSchema = <S extends z.ZodType>(
  innerSchema: S,
  templateName?: TemplateName,
) => {
  const item = z.looseObject({
    id: z.string().min(1),
    [INNER_DATA_KEY]: innerSchema,
    type: z.literal(STORY_ASSET),
  });

  const contentEntry = z.looseObject({
    id: z.string().min(1),
    learningAssetContentItems: z.array(item).length(1),
    type: z.literal(STORY_ASSET),
  });

  const fields = templateName === undefined
    ? EnvelopeFields
    : EnvelopeFields.extend({ templateName: z.literal(templateName) });

  return z.looseObject({
    content: z.array(contentEntry).length(1),
    fields,
    schemaVersion: z.literal(1),
  });
};

/** Pull the inner content out of an envelope. Returns undefined when missing. */
export const getInner = <TInner = unknown>(envelope: unknown): TInner | undefined => {
  const e = envelope as { content?: { learningAssetContentItems?: Record<string, unknown>[] }[] } | null | undefined;
  return e?.content?.[0]?.learningAssetContentItems?.[0]?.[INNER_DATA_KEY] as TInner | undefined;
};

/** Path inside the envelope to the inner data (basePath for rules). */
export const innerBasePath = (): (string | number)[] =>
  ['content', 0, 'learningAssetContentItems', 0, INNER_DATA_KEY];
